import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the RoleDataContext
const RoleDataContext = createContext();

// Custom hook to use the RoleDataContext
export const useRoleData = () => useContext(RoleDataContext);

// Provider component to wrap the app
export const RoleDataProvider = ({ children }) => {
  const [usersByRole, setUsersByRole] = useState({});
  const [counts, setCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = "https://hrmspayroll.prepstripe.com/api/users-by-roles";
  const token = getToken();

  // Main function to fetch all users by roles from API
  const fetchUsersByRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.data?.success) {
        setUsersByRole(response.data?.users || {});
        setCounts(response.data?.counts || {});
        setTotalCount(response.data?.total_count || 0);
      }
    } catch (error) {
      console.error("Error fetching users by roles:", error);
      setError(error.response?.data?.message || "Failed to fetch users by roles.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // Individual role getter functions
  const getSuperAdminUsers = useCallback(() => {
    return usersByRole["Super Admin"] || [];
  }, [usersByRole]);

  const getHRUsers = useCallback(() => {
    return usersByRole["HR"] || [];
  }, [usersByRole]);

  const getManagerUsers = useCallback(() => {
    return usersByRole["Manager"] || [];
  }, [usersByRole]);

  const getEmployeeUsers = useCallback(() => {
    return usersByRole["Employee"] || [];
  }, [usersByRole]);

  const getTeamLeadUsers = useCallback(() => {
    return usersByRole["Team Lead"] || [];
  }, [usersByRole]);

  const getAdminUsers = useCallback(() => {
    return usersByRole["Admin"] || [];
  }, [usersByRole]);

  const getUserUsers = useCallback(() => {
    return usersByRole["User"] || [];
  }, [usersByRole]);

  const getHODUsers = useCallback(() => {
    return usersByRole["HOD"] || [];
  }, [usersByRole]);

  // Fetch users by roles from the API on load
  useEffect(() => {
    if (token) {
      fetchUsersByRoles();
    }
  }, [token, fetchUsersByRoles]);

  // Memoize the context value
  const value = useMemo(
    () => ({
      usersByRole,
      counts,
      totalCount,
      isLoading,
      error,
      fetchUsersByRoles,
      getSuperAdminUsers,
      getHRUsers,
      getManagerUsers,
      getEmployeeUsers,
      getTeamLeadUsers,
      getAdminUsers,
      getUserUsers,
      getHODUsers,
    }),
    [
      usersByRole,
      counts,
      totalCount,
      isLoading,
      error,
      fetchUsersByRoles,
      getSuperAdminUsers,
      getHRUsers,
      getManagerUsers,
      getEmployeeUsers,
      getTeamLeadUsers,
      getAdminUsers,
      getUserUsers,
      getHODUsers,
    ]
  );

  return (
    <RoleDataContext.Provider value={value}>
      {children}
    </RoleDataContext.Provider>
  );
};

